import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { useSelector } from "react-redux";
import {
	ExpansionPanel,
	ExpansionPanelDetails,
	ExpansionPanelSummary,
	Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { JobSiteList } from "features/jobs";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { useRouter } from "utils";

const useStyles = makeStyles((theme) => ({
	root: {
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.white,
		overflow: "hidden",
		boxShadow: "none",
		"&:before": {
			display: "none",
		},
	},
	summary: {
		padding: theme.spacing(0, 1.5, 0, 2),
		minHeight: 52,
		"&.Mui-expanded": {
			minHeight: 52,
		},
		"& .MuiExpansionPanelSummary-content": {
			margin: theme.spacing(1.25, 0),
			alignItems: "center",
		},
	},
	/* Match timesheet list collapsed row primary line (staff name) — theme h6 is 14px */
	summaryTitle: {
		fontWeight: 600,
		fontSize: "1rem",
		color: theme.palette.text.primary,
	},
	countMuted: {
		fontWeight: 500,
		color: theme.palette.text.secondary,
		marginLeft: theme.spacing(0.5),
	},
	expandIcon: {
		color: theme.palette.text.secondary,
	},
	details: {
		padding: 0,
		display: "block",
		overflow: "hidden",
	},
}));

/** Same total as the Photos / Notes list (`job/paged` with siteId filter). */
const readPagedTotal = (payload) => {
	if (payload == null) {
		return null;
	}
	const raw = payload.totalNumberOfRecords ?? payload.TotalNumberOfRecords;
	if (raw === undefined || raw === null) {
		return null;
	}
	const n = Number(raw);
	return Number.isFinite(n) ? n : null;
};

const fetchSiteJobTotal = (organisationId, siteId) =>
	safetyWiseApi
		.post(`${organisationId}/job/paged`, {
			page: 1,
			pageSize: 1,
			orderBy: "createdAt",
			ascending: false,
			searchKey: "",
			columnName: "siteId",
			columnValue: siteId,
		})
		.then((res) => readPagedTotal(res.data));

const SitePhotosNotesPanel = ({
	site,
	organisationId,
	expanded,
	onExpandedChange,
}) => {
	const classes = useStyles();
	const { match } = useRouter();
	const [notesCount, setNotesCount] = useState(null);
	const siteIdFromRoute = match?.params?.siteId;
	/* Prefer site entity id: embedded summary often matches Route `/sites` which has no :siteId param. */
	const siteId = site?.id || siteIdFromRoute;
	const currentOrganisationFromStore = useSelector(
		({ organisations }) => organisations.currentOrganisation
	);
	const organisationIdResolved = useMemo(() => {
		if (organisationId) {
			return organisationId;
		}
		const co = currentOrganisationFromStore;
		if (typeof co === "string") {
			return co;
		}
		if (co && co.id) {
			return co.id;
		}
		return "";
	}, [organisationId, currentOrganisationFromStore]);

	useEffect(() => {
		let cancelled = false;
		if (!organisationIdResolved || !siteId) {
			setNotesCount(null);
			return undefined;
		}
		fetchSiteJobTotal(organisationIdResolved, siteId)
			.then((total) => {
				if (!cancelled) {
					setNotesCount(total);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setNotesCount(null);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [organisationIdResolved, siteId]);

	useEffect(() => {
		if (!expanded || !organisationIdResolved || !siteId) {
			return undefined;
		}
		let cancelled = false;
		fetchSiteJobTotal(organisationIdResolved, siteId)
			.then((total) => {
				if (!cancelled) {
					setNotesCount(total);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setNotesCount(null);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [expanded, organisationIdResolved, siteId]);

	if (!site || !siteId) {
		return null;
	}

	const countLabel =
		notesCount !== null && notesCount !== undefined
			? ` (${notesCount})`
			: "";

	return (
		<ExpansionPanel
			className={classes.root}
			elevation={0}
			expanded={expanded}
			onChange={onExpandedChange}
			data-cy="site-photos-notes-panel"
		>
			<ExpansionPanelSummary
				expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
				className={classes.summary}
				aria-controls="site-photos-notes-panel-content"
				id="site-photos-notes-panel-header"
			>
				<Typography
					component="h2"
					className={classes.summaryTitle}
				>
					Photos / Notes
					{countLabel !== "" && (
						<span className={classes.countMuted}>{countLabel}</span>
					)}
				</Typography>
			</ExpansionPanelSummary>
			<ExpansionPanelDetails className={classes.details}>
				{expanded && site?.id && (
					<JobSiteList variant="embedded" siteId={site.id} />
				)}
			</ExpansionPanelDetails>
		</ExpansionPanel>
	);
};

SitePhotosNotesPanel.propTypes = {
	site: PropTypes.object,
	organisationId: PropTypes.string,
	expanded: PropTypes.bool.isRequired,
	onExpandedChange: PropTypes.func.isRequired,
};

export default SitePhotosNotesPanel;
